import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { phoneTracker } from '../lib/phoneTracking';
import { hasAnalyticsConsent } from '../lib/analytics';
import { logger } from '../utils/logger';

interface PhoneTrackingContextType {
  trackPhoneClick: (element: HTMLElement) => void;
  isTrackingEnabled: boolean;
}

const PhoneTrackingContext = createContext<PhoneTrackingContextType | null>(null);

export function usePhoneTracking() {
  const context = useContext(PhoneTrackingContext);
  if (!context) {
    throw new Error('usePhoneTracking must be used within PhoneTrackingProvider');
  }
  return context;
}

interface PhoneTrackingProviderProps {
  children: ReactNode;
}

export function PhoneTrackingProvider({ children }: PhoneTrackingProviderProps) {
  const isTrackingEnabled = hasAnalyticsConsent() === true;

  useEffect(() => {
    if (!isTrackingEnabled) {
      logger.info('Phone tracking disabled - no analytics consent');
      return;
    }

    // Initialize phone tracking
    logger.info('Phone tracking initialized');

    // Set up automated alerts check (every hour)
    const alertInterval = setInterval(() => {
      phoneTracker.generateAlerts().catch(logger.error);
    }, 60 * 60 * 1000); // 1 hour

    return () => {
      clearInterval(alertInterval);
    };
  }, [isTrackingEnabled]);

  const trackPhoneClick = (element: HTMLElement) => {
    if (isTrackingEnabled) {
      logger.debug('Phone click tracked for element');
      // The actual tracking is handled automatically by the phoneTracker singleton
    }
  };

  const value: PhoneTrackingContextType = {
    trackPhoneClick,
    isTrackingEnabled
  };

  return (
    <PhoneTrackingContext.Provider value={value}>
      {children}
    </PhoneTrackingContext.Provider>
  );
}